<template>
  <p class="font-bold mb-4">Opportunity Owner</p>
  <p class="text-sm mb-24 text-neutral-text">
    Define what field in your CRM defines the opportunity owner
  </p>

  <BittsSelect
    v-model="selectedOpportunityOwner"
    :options
    id="opportunity_owner"
    class="opportunity_owner__selector"
    placeholder="Opportunity Owner"
    form-label="Select opportunity owner field"
    @update:model-value="onOpportunityOwnerChange"
  >
    <template #suffix="{ option }">
      <BittsTag
        v-if="option.default"
        color="info"
        size="x-small"
        variant="rounded"
        class="mr-8 w-full"
      >
        Default
      </BittsTag>
    </template>
  </BittsSelect>

  <BittsDivider class="my-24" />
</template>

<script setup lang="ts">
import { BittsDivider, BittsSelect, BittsTag } from '@crossbeam/bitts';

import { computed, onMounted, ref, watch } from 'vue';

import { usePreferredCRMFeed } from '@/composables/usePreferredCRMFeed';
import { MDM_PROPERTIES } from '@/constants/mdm';
import { useFeedsStore, useSourcesStore } from '@/stores';

const props = defineProps<{
  sourceFieldId: number | null;
}>();

const emit =
  defineEmits<
    (e: 'opportunity-owner-changed', sourceFieldId: number) => void
  >();

const selectedOpportunityOwner = ref<number | null>(null);

const sourcesStore = useSourcesStore();

function onOpportunityOwnerChange(value: number) {
  selectedOpportunityOwner.value = value;
  emit('opportunity-owner-changed', value);
}

const { preferredCRMFeed } = usePreferredCRMFeed();

const options = computed(() => {
  const feedId = preferredCRMFeed.value?.id;
  if (!feedId) return [];
  return sourcesStore.dealSources
    .filter((src) => src.feed_id === feedId)
    .flatMap((src) => src.fields)
    .filter((f) => f.pg_data_type === 'text' && f.is_visible)
    .sort((a, b) => a.display_name.localeCompare(b.display_name))
    .map((f) => ({
      mdmProperty: f.mdm_property,
      value: f.id,
      label: f.display_name,
      default: f.mdm_property === MDM_PROPERTIES.deal.OWNER_ID,
    }));
});

const feedsStore = useFeedsStore();

function setupOptions() {
  if (props.sourceFieldId) {
    const foundField = options.value.find(
      (item) => item.value === props.sourceFieldId,
    );
    if (foundField && foundField.value) {
      selectedOpportunityOwner.value = foundField.value;
    }
  } else {
    const defaultField = options.value.find(
      (item) => item.mdmProperty === MDM_PROPERTIES.deal.OWNER_ID,
    );
    if (defaultField && defaultField.value) {
      selectedOpportunityOwner.value = defaultField.value;
    }
  }
}
onMounted(async () => {
  await sourcesStore.readySync;
  await feedsStore.readySync;
  setupOptions();
});

watch(
  () => props.sourceFieldId,
  () => {
    setupOptions();
  },
);
</script>
<style lang="scss">
.opportunity_owner__selector {
  @apply w-[280px];
  .ant-select .ant-select-selector {
    .ant-select-selection-item div {
      @apply w-full;
      div {
        @apply m-auto flex-1;
      }
    }
  }
}
</style>
